import { useLanguage } from '../context/LanguageContext';
import { adminTranslations, getAdminText } from './adminTranslations';

/**
 * Custom hook for admin panel translations
 * Combines the main language context with admin-specific translations
 */
export const useAdminTranslation = () => {
  const { language, t: mainT } = useLanguage();
  
  /**
   * Get admin translation by key path
   * @param {string} key - Dot-separated key path (e.g., 'users.title', 'common.save')
   * @param {object} params - Optional parameters for interpolation
   * @returns {string} Translated text or key if not found
   */
  const at = (key, params = {}) => {
    let text = getAdminText(key, language);
    
    // Handle parameter interpolation
    if (params && typeof text === 'string') {
      Object.keys(params).forEach(param => {
        text = text.replace(new RegExp(`{${param}}`, 'g'), params[param]);
      });
    }
    
    return text;
  };
  
  /**
   * Get text based on language condition
   * Useful for inline translations
   * @param {string} esText - Spanish text
   * @param {string} enText - English text
   * @returns {string} Text for current language
   */
  const tx = (esText, enText) => {
    return language === 'es' ? esText : (enText || esText);
  };
  
  /**
   * Get all translations for a section
   * @param {string} section - Section name (e.g., 'users', 'orders')
   * @returns {object} All translations for the section
   */
  const getSection = (section) => {
    return adminTranslations[language]?.[section] || adminTranslations.es?.[section] || {};
  };
  
  return {
    language,
    at,      // Admin translation function
    tx,      // Quick inline translation
    t: mainT, // Main translations from context
    getSection,
    isSpanish: language === 'es',
    isEnglish: language === 'en',
  };
};

export default useAdminTranslation;
